import React, { useState, useEffect } from 'react';
import {
    CalendarDaysIcon,
    CurrencyDollarIcon,
    BuildingOfficeIcon,
    ClockIcon,
    DocumentTextIcon,
    UserGroupIcon,
    ChartBarIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { authorizedFetch } from '../utils/api';

const Planning = () => {
    const [activeTab, setActiveTab] = useState('calendar');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [renderTime, setRenderTime] = useState(null);

    // State for different planning sections
    const [buyingCalendar, setBuyingCalendar] = useState([]);
    const [budgetPlans, setBudgetPlans] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [reports, setReports] = useState([]);

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        const startTime = performance.now();
        fetchPlanningData().finally(() => {
            const endTime = performance.now();
            setRenderTime(Math.round(endTime - startTime));
        });
    }, []);

    const fetchPlanningData = async () => {
        setLoading(true);
        try {
            const [calendarResp, budgetResp, vendorsResp, meetingsResp, tasksResp] = await Promise.all([
                authorizedFetch(`${backendUrl}/planning/calendar`),
                authorizedFetch(`${backendUrl}/planning/budget`),
                authorizedFetch(`${backendUrl}/planning/vendors`),
                authorizedFetch(`${backendUrl}/planning/meetings`),
                authorizedFetch(`${backendUrl}/planning/tasks`)
            ]);

            setBuyingCalendar(calendarResp.data || []);
            setBudgetPlans(budgetResp.data || []);
            setVendors(vendorsResp.data || []);
            setMeetings(meetingsResp.data || []);
            setTasks(tasksResp.data || []);
        } catch (err) {
            console.error('Error fetching planning data:', err);
            setError('Failed to load planning data');
        } finally {
            setLoading(false);
        }
    };

    const LoadingSkeleton = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const CalendarView = () => (
        <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <CalendarDaysIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">12</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Upcoming Events</div>
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <ClockIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">5</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">This Week</div>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">3</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Urgent Deadlines</div>
                        </div>
                    </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <BuildingOfficeIcon className="h-8 w-8 text-purple-600 dark:text-purple-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">8</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Fashion Weeks</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Calendar Events */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Buying Calendar - Spring/Summer 2024
                        </h3>
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center">
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Event
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    <div className="space-y-4">
                        {[
                            {
                                date: "Feb 15-20, 2024",
                                event: "Milan Fashion Week",
                                type: "Fashion Week",
                                priority: "High",
                                status: "Confirmed",
                                details: "Attend shows: Prada, Versace, Bottega Veneta"
                            },
                            {
                                date: "Feb 25-Mar 5, 2024",
                                event: "Paris Fashion Week",
                                type: "Fashion Week",
                                priority: "High",
                                status: "Confirmed",
                                details: "Key appointments: Chanel, Dior, Saint Laurent"
                            },
                            {
                                date: "Mar 10, 2024",
                                event: "Spring Collection Review",
                                type: "Internal Meeting",
                                priority: "Medium",
                                status: "Scheduled",
                                details: "Review buying decisions with merchandising team"
                            },
                            {
                                date: "Mar 15, 2024",
                                event: "Budget Approval Deadline",
                                type: "Deadline",
                                priority: "High",
                                status: "Pending",
                                details: "Submit final budget for SS24 season"
                            },
                            {
                                date: "Apr 1-3, 2024",
                                event: "Coterie Trade Show",
                                type: "Trade Show",
                                priority: "Medium",
                                status: "Registered",
                                details: "Focus on contemporary brands and emerging designers"
                            }
                        ].map((event, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center mb-2">
                                            <h4 className="text-lg font-medium text-gray-900 dark:text-white mr-3">
                                                {event.event}
                                            </h4>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${event.priority === 'High' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                                event.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                                    'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                                }`}>
                                                {event.priority}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                            📅 {event.date} • 🏷️ {event.type} • ✅ {event.status}
                                        </div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                            {event.details}
                                        </p>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        <button className="p-2 text-gray-400 hover:text-blue-600">
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button className="p-2 text-gray-400 hover:text-red-600">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const BudgetView = () => (
        <div className="space-y-6">
            {/* Budget Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <CurrencyDollarIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">$2.5M</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total Budget</div>
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <ChartBarIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">$1.8M</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Allocated</div>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <div className="h-8 w-8 bg-yellow-600 dark:bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                            <span className="text-white text-sm font-bold">%</span>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">72%</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Utilized</div>
                        </div>
                    </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <div className="h-8 w-8 bg-purple-600 dark:bg-purple-400 rounded-full flex items-center justify-center mr-3">
                            <span className="text-white text-sm font-bold">OTB</span>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">$700K</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Open to Buy</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Budget Allocation by Category */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Budget Allocation - Spring/Summer 2024
                    </h3>
                </div>
                <div className="p-6">
                    <div className="space-y-4">
                        {[
                            { category: "Dresses", budget: 450000, spent: 320000, percentage: 18, color: "#3B82F6" },
                            { category: "Outerwear", budget: 380000, spent: 285000, percentage: 15, color: "#10B981" },
                            { category: "Tops & Blouses", budget: 350000, spent: 245000, percentage: 14, color: "#F59E0B" },
                            { category: "Bottoms", budget: 320000, spent: 240000, percentage: 13, color: "#EF4444" },
                            { category: "Knitwear", budget: 280000, spent: 195000, percentage: 11, color: "#8B5CF6" },
                            { category: "Accessories", budget: 250000, spent: 180000, percentage: 10, color: "#06B6D4" },
                            { category: "Footwear", budget: 220000, spent: 165000, percentage: 9, color: "#84CC16" },
                            { category: "Bags", budget: 200000, spent: 140000, percentage: 8, color: "#F97316" },
                            { category: "Jewelry", budget: 150000, spent: 105000, percentage: 6, color: "#EC4899" }
                        ].map((item, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                                        {item.category}
                                    </h4>
                                    <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            ${item.spent.toLocaleString()} / ${item.budget.toLocaleString()}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {Math.round((item.spent / item.budget) * 100)}% utilized
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full transition-all duration-300"
                                        style={{
                                            width: `${(item.spent / item.budget) * 100}%`,
                                            backgroundColor: item.color
                                        }}
                                    ></div>
                                </div>
                                <div className="flex justify-between items-center mt-2 text-xs text-gray-600 dark:text-gray-400">
                                    <span>{item.percentage}% of total budget</span>
                                    <span>Remaining: ${(item.budget - item.spent).toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const VendorsView = () => (
        <div className="space-y-6">
            {/* Vendor Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">45</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Active Vendors</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">12</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Key Partners</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">8</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">New This Season</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">95%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">On-Time Delivery</div>
                </div>
            </div>

            {/* Vendor List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Vendor Management
                        </h3>
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center">
                            <PlusIcon className="h-4 w-4 mr-2" />
                            Add Vendor
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Brand</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Contact</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Category</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Relationship</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Performance</th>
                                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    {
                                        brand: "Ganni",
                                        contact: "Sarah Johnson",
                                        email: "sarah@ganni.com",
                                        category: "Contemporary",
                                        relationship: "Key Partner",
                                        performance: "Excellent",
                                        rating: 9.2
                                    },
                                    {
                                        brand: "& Other Stories",
                                        contact: "Maria Garcia",
                                        email: "maria@stories.com",
                                        category: "Contemporary",
                                        relationship: "Regular",
                                        performance: "Good",
                                        rating: 8.5
                                    },
                                    {
                                        brand: "Acne Studios",
                                        contact: "Erik Lundberg",
                                        email: "erik@acnestudios.com",
                                        category: "Designer",
                                        relationship: "Key Partner",
                                        performance: "Excellent",
                                        rating: 9.0
                                    },
                                    {
                                        brand: "COS",
                                        contact: "Anna Chen",
                                        email: "anna@cos.com",
                                        category: "Minimalist",
                                        relationship: "Regular",
                                        performance: "Good",
                                        rating: 8.3
                                    }
                                ].map((vendor, index) => (
                                    <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                                        <td className="py-3 px-4">
                                            <div className="font-medium text-gray-900 dark:text-white">{vendor.brand}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="text-sm text-gray-900 dark:text-white">{vendor.contact}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{vendor.email}</div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full text-xs">
                                                {vendor.category}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className={`px-2 py-1 rounded-full text-xs ${vendor.relationship === 'Key Partner'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                                }`}>
                                                {vendor.relationship}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center">
                                                <span className={`text-sm font-medium ${vendor.performance === 'Excellent' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
                                                    }`}>
                                                    {vendor.performance}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                                    ({vendor.rating}/10)
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex space-x-2">
                                                <button className="p-1 text-gray-400 hover:text-blue-600">
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <button className="p-1 text-gray-400 hover:text-green-600">
                                                    <CheckCircleIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );

    const tabs = [
        { id: 'calendar', label: 'Buying Calendar', icon: CalendarDaysIcon },
        { id: 'budget', label: 'Budget Planning', icon: CurrencyDollarIcon },
        { id: 'vendors', label: 'Vendor Management', icon: BuildingOfficeIcon },
        { id: 'meetings', label: 'Meetings & Tasks', icon: UserGroupIcon },
        { id: 'reports', label: 'Reports', icon: DocumentTextIcon }
    ];

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        Professional Buying Planning
                    </h1>
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                </div>
                <LoadingSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                <button
                    onClick={fetchPlanningData}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    Professional Buying Planning
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Comprehensive planning tools for professional fashion buyers. Manage your buying calendar,
                    budget allocation, vendor relationships, and team coordination in one integrated platform.
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                        }`}
                                >
                                    <Icon className="h-5 w-5 mr-2" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                <div className="p-6">
                    {activeTab === 'calendar' && <CalendarView />}
                    {activeTab === 'budget' && <BudgetView />}
                    {activeTab === 'vendors' && <VendorsView />}
                    {activeTab === 'meetings' && (
                        <div className="text-center py-8">
                            <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Meetings & Tasks
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Meeting scheduler and task management coming soon.
                            </p>
                        </div>
                    )}
                    {activeTab === 'reports' && (
                        <div className="text-center py-8">
                            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Professional Reports
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                Executive reporting and analytics coming soon.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Performance Info */}
            {renderTime && (
                <div className="text-center text-xs text-gray-500 dark:text-gray-400">
                    Loaded in {renderTime}ms
                </div>
            )}
        </div>
    );
};

export default Planning;
